<template>
  <div class="pie-chart">
    <h2 v-if="headline" class="pie-chart__title">{{ headline }}</h2>
    <slot name="content"></slot>
    <div class="pie-chart__container">
      <canvas class="pie-chart__canvas" ref="chartCanvas"></canvas>
    </div>
  </div>
</template>

<script lang="ts">
  import Chart, { type ChartConfiguration } from 'chart.js'
  import 'chartjs-adapter-date-fns'
  import { useUnits } from '@/composables/useUnits.ts'
  import { ref, type Ref } from 'vue'
  import { useHistoryStore } from '@/stores/useHistoryStore'

  export default {
    name: 'pie-chart',
    props: {
      headline: {
        type: String,
      },
      dataSource: {
        type: String,
        required: true,
      },
      minEnergyAmount: {
        type: Number,
        required: false,
        default: 0
      },
    },
    setup() {
      const historyStore = useHistoryStore()
      const { formatKWH } = useUnits()

      const config: Ref<ChartConfiguration> = ref({
        type: 'pie',
        options: {
          maintainAspectRatio: false,
          fontColor: '#dddddd',
          responsive: true,
          legend: {
            position: 'left',
            maxWidth: 100,
            display: true,
            labels: {
              filter: (item) => {
                return item.index < 6
              },
              generateLabels(chart) {
                const data = chart.data
                let result = []

                if (data.datasets.length > 0) {
                  const meta = chart.getDatasetMeta(0)

                  result = data.labels
                    .map((label, index) => {
                      const energy = (data.datasets[0].data[index] as number) ?? 0

                      // Completely skip zero or invalid values
                      if (energy === 0 || isNaN(energy)) {
                        return null
                      }

                      const style = meta.controller.getStyle(index)

                      return {
                        text: `${label} (${formatKWH(energy * 1000)})`,
                        fillStyle: style.backgroundColor,
                        strokeStyle: style.borderColor,
                        lineWidth: style.borderWidth,
                        hidden: meta.data[index].hidden,
                        index,
                      }
                    })
                    .filter(item => item !== null)
                }

                return result
              },
            },
          },
          tooltips: {
            mode: 'index',
            callbacks: {
              label: (item, data) => {
                const value = data.datasets[item.datasetIndex].data[item.index] as number

                return `${data.labels[item.index]} (${formatKWH(value * 1000)})`
              },
            },
          },
        },
        data: {
          labels: [],
          datasets: [
            {
              fill: 'origin',
              backgroundColor: import.meta.env.VITE_SCSS_COLOR_COMMUNITY,
              borderColor: "#FFF",
              data: [],
            },
          ],
        },
      })

      const chart: Ref<Chart | null> = ref(null)
      const chartCanvas: Ref<HTMLCanvasElement | null> = ref(null)

      return {
        config,
        historyStore,
        chart,
        chartCanvas
      }
    },
    mounted() {
      this.updateChart(this.chartData)
    },
    computed: {
      chartData() {
        return this.historyStore.getTradingStatistics(this.dataSource)
      },
    },
    methods: {
      updateChart: function (data) {
        // Dont show/update the chart without any data.
        if (!data || data.labels.length < 1) {
          return
        }

        // only filter small values if there is at least one larger one, otherwise the charts would be empty
        const applyMinEnergyFilter = (data?.data?.filter(value => value >= this.minEnergyAmount)?.length ?? 0) > 0;

        const filteredData = [];
        const filteredLabels = [];
        const filteredBackgroundColors = []

        // filter out all energy trades that are below a certain threshold
        for (let x = 0; x < (data?.data?.length ?? 0); x++) {
          const value = data?.data?.[x];
          const label = data?.labels?.[x];
          const backgroundColor = data?.backgroundColor?.[x];

          if (!isNaN(value) && (!applyMinEnergyFilter || value >= this.minEnergyAmount)) {
            filteredData.push(value);
            filteredLabels.push(label);
            filteredBackgroundColors.push(backgroundColor);
          }
        }

        if (!this.chart) {
          let chartSettings = {
            ...this.config,
          }

          chartSettings.data.labels = filteredLabels
          chartSettings.data.datasets[0].data = filteredData
          chartSettings.data.datasets[0].backgroundColor = filteredBackgroundColors

          this.chart = new Chart(this.chartCanvas, chartSettings)
        } else {
          this.chart.data.labels = filteredLabels
          this.chart.data.datasets[0].data = filteredData
          this.chart.data.datasets[0].backgroundColor = filteredBackgroundColors
          this.chart.update()
        }

        console.debug(`PieChart data '${this.headline}'`, this.chart.data);
      },
    },
    watch: {
      chartData: {
        handler(newData) {
          this.updateChart(newData)
        },
        deep: true
      }
    }
  }
</script>

<style lang="scss">
  .pie-chart {
    &__title {
      margin-bottom: $spacer01;
    }

    &__content {
      margin-bottom: $spacer10;
    }
  }
</style>
